import { useId, useState } from "react";
import { Navigate, useLocation, useHref } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { setMockSession } from "../auth/auth";
import { PoweredBy, StarsachMark } from "../components/Brand";
import { LoadingState, inputClass } from "../components/ui";
import { isMock } from "../config";
import { ssoRouteFor } from "../lib/sso";

export function LoginPage() {
  const { status, signIn, config } = useAuth();
  const location = useLocation();
  // useHref folds in the router's basename, so a view-as tab (ADR-0011) comes back to the customer's portal, not the root.
  const from = useHref((location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/");
  if (status === "signed-in") return <Navigate to={from} replace />;
  // A session may already exist (the callback just completed, or a reload):
  // showing the card while /v1/me is fetched reads as being signed out again.
  if (status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-canvas dark:bg-neutral-950">
        <LoadingState label="Signing in" />
      </main>
    );
  }

  return (
    // A single quiet card on a light field, the way tableau.com's sign-in reads:
    // mark, one heading, one action, nothing competing with it.
    <main className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6 py-12 dark:bg-neutral-950">
      <div className="w-full max-w-md rounded-2xl bg-white px-8 py-10 shadow-lg ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="flex flex-col items-center text-center">
          <StarsachMark className="h-10 w-10 text-brand" />
          <h1 className="mt-5 text-2xl font-semibold">Sign in to Starsach</h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">Multi-factor authentication is required.</p>
        </div>
        {config && config.ssoDomainHashes.length > 0 && <EmailFirst domainHashes={config.ssoDomainHashes} onRoute={(idpIdentifier) => void signIn(from, idpIdentifier ? { idpIdentifier } : undefined)} />}
        {isMock ? (
          <div className="mt-8 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Mock sign-in</p>
            <button type="button" className="w-full rounded-lg bg-accent px-4 py-3 text-base font-medium text-white hover:bg-accent-strong" onClick={() => { setMockSession("internal-team"); window.location.assign(from); }}>
              Continue as internal team
            </button>
            <button type="button" className="w-full rounded-lg border border-neutral-300 px-4 py-3 text-base font-medium text-ink hover:bg-neutral-50 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-800" onClick={() => { setMockSession("customer", "org-acme", ["cust-acme"]); window.location.assign(from); }}>
              Continue as customer (ACME)
            </button>
            <button type="button" className="w-full rounded-lg border border-neutral-300 px-4 py-3 text-base font-medium text-ink hover:bg-neutral-50 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-800" onClick={() => { setMockSession("customer", "org-acme", ["cust-acme"], "org-acme"); window.location.assign(from); }}>
              Continue as customer (ACME, SSO)
            </button>
          </div>
        ) : (
          !config?.ssoDomainHashes.length && (
            <button type="button" className="mt-8 w-full rounded-lg bg-accent px-4 py-3 text-base font-medium text-white hover:bg-accent-strong" onClick={() => void signIn(from)}>
              Sign in
            </button>
          )
        )}
      </div>
      <PoweredBy className="mt-8" />
    </main>
  );
}

/**
 * ADR-0014: an email first, so an organisation with its own identity
 * provider goes straight to it and nobody else learns it exists. The
 * address is hashed and compared in the browser; it is not sent to us.
 */
function EmailFirst({ domainHashes, onRoute }: { domainHashes: readonly string[]; onRoute: (idpIdentifier: string | undefined) => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const id = useId();
  return (
    <form
      className="mt-8 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        void ssoRouteFor(email, domainHashes)
          .then(onRoute)
          .finally(() => setBusy(false));
      }}
    >
      <label htmlFor={id} className="block text-sm font-medium">
        Email address
      </label>
      <input id={id} type="email" autoComplete="username" inputMode="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required aria-describedby={`${id}-hint`} />
      <p id={`${id}-hint`} className="text-xs text-neutral-500">If your organisation signs in with its own identity provider, you will be taken there.</p>
      <button type="submit" className="w-full rounded-lg bg-accent px-4 py-3 text-base font-medium text-white hover:bg-accent-strong disabled:opacity-60" disabled={busy}>
        Continue
      </button>
    </form>
  );
}
